import { Fragment, useEffect, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { useTranslation } from 'react-i18next'
import { XIcon } from '@heroicons/react/solid'
import { Loader } from '../../../components'
import { fetchCycleCountFilterOptions } from '../../../services/cycleCountServices'
import { createCoverageEvent } from '../../../services/coverageEventServices'
import { BulkAssignFiltersStep } from '../BulkAssignFiltersStep'
import { CoverageEventPreviewStep } from './CoverageEventPreviewStep'
import { mapCoverageEventError } from './coverageEventErrors'

const T = 'cycle_counts.events.create'

// Filter keys the platform accepts; location_type is deliberately not exposed in the dialog yet
// (no control for it in BulkAssignFiltersStep — Extrema's pilot warehouse is ~99% PICKING).
const cleanFilters = filters => {
  const out = {}
  for (const key of ['store_id', 'area', 'row']) if (filters?.[key]?.length) out[key] = filters[key]
  return out
}

export const CreateCoverageEventDialog = ({ open, warehouseId, onCancel, onSuccess, onGateOff }) => {
  const { i18n } = useTranslation()
  const [step, setStep] = useState('filters')
  const [filters, setFilters] = useState({ area: [], row: [], store_id: [] })
  const [previewFilters, setPreviewFilters] = useState(null)
  const [filterOptions, setFilterOptions] = useState(null)
  const [signature, setSignature] = useState(null)
  const [busy, setBusy] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [filterOptionsError, setFilterOptionsError] = useState(null)

  useEffect(() => {
    if (!open) {
      setStep('filters')
      setFilters({ area: [], row: [], store_id: [] })
      setPreviewFilters(null)
      setSignature(null)
      setSubmitError(null)
      setFilterOptions(null)
      setFilterOptionsError(null)
      return
    }
    setFilterOptionsError(null)
    fetchCycleCountFilterOptions(warehouseId)
      .then(setFilterOptions)
      .catch(err => {
        // Keep the dialog usable with empty dropdowns rather than stuck on the loader —
        // but say why they're empty instead of leaving the supervisor to guess.
        setFilterOptions({ areas: [], rows_by_area: {}, stores: [] })
        setFilterOptionsError(mapCoverageEventError(err, i18n).message)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, warehouseId])

  const goToPreview = () => {
    // Freeze the filters the preview was run against — going back and changing them must not
    // silently change what create() sends without another preview.
    setPreviewFilters(cleanFilters(filters))
    setStep('preview')
  }

  const goBackToFilters = () => {
    setPreviewFilters(null)
    setStep('filters')
  }

  const confirm = async () => {
    setBusy(true)
    setSubmitError(null)
    try {
      const created = await createCoverageEvent({ warehouse_id: warehouseId, filters: previewFilters, merchant_signature_required: signature })
      onSuccess(created)
    } catch (err) {
      const mapped = mapCoverageEventError(err, i18n)
      if (mapped.gateOff) onGateOff()
      setSubmitError(mapped.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" onClose={busy ? () => {} : onCancel}>
        <div className="flex items-center justify-center min-h-screen">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="relative bg-white rounded-lg shadow-xl sm:max-w-2xl sm:w-full p-6 text-gray-600 my-3">
              {!busy && (
                <div className="absolute top-0 right-0 p-5 cursor-pointer" onClick={onCancel}>
                  <XIcon className="h-5 w-5" />
                </div>
              )}
              <Dialog.Title className="text-lg text-center mb-4">{i18n.t(`${T}.title`)}</Dialog.Title>

              {step === 'filters' ? (
                filterOptions === null ? (
                  <div className="flex justify-center py-12">
                    <Loader show />
                  </div>
                ) : (
                  <>
                    {filterOptionsError && <p className="text-sm text-red-700 mb-3 text-center">{filterOptionsError}</p>}
                    <BulkAssignFiltersStep
                      filterOptions={filterOptions}
                      filters={filters}
                      onFiltersChange={setFilters}
                      onPreview={goToPreview}
                      onCancel={onCancel}
                      loading={false}
                    />
                  </>
                )
              ) : (
                <CoverageEventPreviewStep
                  warehouseId={warehouseId}
                  filters={previewFilters}
                  signature={signature}
                  onSignatureChange={setSignature}
                  onBack={goBackToFilters}
                  onConfirm={confirm}
                  busy={busy}
                  onGateOff={onGateOff}
                  submitError={submitError}
                />
              )}
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  )
}
